import { ApolloClient, ApolloQueryResult } from '@apollo/client';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { get as _get } from 'lodash';
import { RootState } from 'store';

import { ERROR_MESSAGES } from '../../constants/ErrorMessages';
import { assignGrant } from '../../gql/queries/assignGrant';
import { checkIsActionAllowed } from '../../gql/queries/checkIsActionAllowed';
import { createVendorUser } from '../../gql/queries/createUser';
import {
  getUsersForBusinessEntityIdQuery,
  GetUsersForBusinessEntityIdResult
} from '../../gql/queries/getUsersForBusinessEntityIdQuery';
import { getVendorQuery, GetVendorQueryResult } from '../../gql/queries/getVendorQuery';
import {
  ActorTypes,
  PermissionActionClass,
  UserInfo,
  VendorUserInfo
} from '../pages/RFXRequest/steps/RFXRequestSuppliersStep/types';

/**
 * Redux state for the CreateContactSlice.
 *
 * @typedef {Object} CreateContactState
 * @property {boolean} isLoading - Submit button state, when isLoading, prevent user interactions.
 * @property {boolean} isSuccessful - Flag to show when contact creation is successful.
 * @property {Object} newContact - Object containing the new contact information.
 * @property {string} newContact.username - Username of the new contact.
 * @property {string} newContact.givenName - Given name of the new contact.
 * @property {string} newContact.familyName - Family name of the new contact.
 * @property {string[]} newContact.vendorIds - Array of vendor IDs associated with the new contact.
 * @property {string} newContact.userId - User ID of the new contact.
 * @property {string[]} newContact.emails - Array of email addresses associated with the new contact.
 */

export interface CreateContactState {
  isLoading: boolean;
  isSuccessful: boolean;
  isSuccessAlertVisible: boolean;
  isDuplicateError: boolean;
  isDisabledUserError: boolean;
  isUserExistsError: boolean;
  newContact: {
    username: string;
    givenName: string;
    familyName: string;
    vendorIds: string[];
    userId: string;
    emails: string[];
  };
  pendingContacts: string[];
  hasInvitePermission: boolean;
}

/**
 * Initial state for the CreateContactSlice.
 */
export const initialState: CreateContactState = {
  isLoading: false,
  isSuccessful: false,
  isSuccessAlertVisible: false,
  isDuplicateError: false,
  isDisabledUserError: false,
  isUserExistsError: false,
  newContact: {
    username: '',
    givenName: '',
    familyName: '',
    vendorIds: [],
    userId: '',
    emails: []
  },
  pendingContacts: [],
  hasInvitePermission: false
};

/**
 * CreateContactSlice for managing the state related to creating a new contact.
 */
const createContactSlice = createSlice({
  name: 'createContact',
  initialState,
  reducers: {
    initCreateContact: () => {
      return initialState;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setIsSuccessful: (state, action) => {
      state.isSuccessful = action.payload;
    },
    setIsSuccessAlertVisible: (state, action) => {
      state.isSuccessAlertVisible = action.payload;
    },
    setIsDuplicateError: (state, action) => {
      state.isDuplicateError = action.payload;
    },
    setIsDisabledUserError: (state, action) => {
      state.isDisabledUserError = action.payload;
    },
    setIsUserExistsError: (state, action) => {
      state.isUserExistsError = action.payload;
    },
    addPendingContact: (state, action) => {
      state.pendingContacts.push(action.payload);
    },
    removePendingContacts: (state, action) => {
      state.pendingContacts = state.pendingContacts.filter((email) => !action.payload.includes(email));
    },
    resetState: () => initialState,
    setHasInvitePermission: (state, action) => {
      state.hasInvitePermission = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkHasPermission.fulfilled, () => {})
      .addCase(submitInviteContact.fulfilled, (state) => {
        state.newContact = initialState.newContact;
      });
  }
});

// Destructures the actions from the createContactSlice.
export const {
  initCreateContact,
  setIsLoading,
  setIsSuccessful,
  setIsSuccessAlertVisible,
  setIsDuplicateError,
  setIsDisabledUserError,
  setIsUserExistsError,
  addPendingContact,
  removePendingContacts,
  resetState,
  setHasInvitePermission
} = createContactSlice.actions;

/**
 * Selector function to retrieve the isLoading state from the Redux store.
 *
 * @param {Object} state - The Redux store state.
 * @returns {boolean} The isLoading state.
 */
export const selectIsLoading = (state: RootState) => state.createContact.isLoading;

/**
 * Selector function to retrieve the isSuccessful state from the Redux store.
 *
 * @param {Object} state - The Redux store state.
 * @returns {boolean} The isSuccessful state.
 */
export const selectIsSuccessful = (state: RootState) => state.createContact.isSuccessful;

export const selectIsSuccessAlertVisible = (state: RootState) => state.createContact.isSuccessAlertVisible;

/**
 * Selector function to retrieve the isSuccessful state from the Redux store.
 *
 * @param {Object} state - The Redux store state.
 * @returns {boolean} The isSuccessful state.
 */
export const selectIsDuplicateError = (state: RootState): boolean => state.createContact.isDuplicateError;

export const selectIsDisabledUserError = (state: RootState): boolean => state.createContact.isDisabledUserError;

export const selectIsUserExistsError = (state: RootState): boolean => state.createContact.isUserExistsError;

/**
 * Selector function to retrieve the pendingContacts state from the Redux store.
 *
 * @param {Object} state - The Redux store state.
 * @returns {string[]} The pendingContacts state.
 */
export const selectPendingContacts = (state: RootState): string[] => state.createContact.pendingContacts;

export const selectHasPermission = (state: RootState) => state.createContact.hasInvitePermission;

export const checkHasPermission = createAsyncThunk(
  'createContact/checkHasPermission',
  async ({ apolloClient, username }: { apolloClient: ApolloClient<object>; username: string }, { dispatch }) => {
    try {
      const response = await checkIsActionAllowed({
        client: apolloClient,
        permissions: ['ocean-wave:CreateUser', 'ocean-wave:AssignGrant'],
        user: username
      });

      dispatch(setHasInvitePermission(response));
    } catch (error) {
      console.error(error);
      dispatch(setHasInvitePermission(false));
    }
  }
);

/**
 * Async thunk for submitting an invite contact request.
 *
 * @param {Object} param0 - An object containing the required parameters.
 * @param {string} param0.creator - The creator of the new contact.
 * @param {Object} param0.newContact - The new contact information.
 * @param {string} param0.supplierId - The supplier ID associated with the new contact.
 * @param {Object} param0.apolloClient - The Apollo client instance.
 * @param {Function} param1.dispatch - The Redux dispatch function.
 * @returns {Promise<void>} A Promise that resolves when the operation is completed.
 */
export const submitInviteContact = createAsyncThunk(
  'createContact/submitInviteContact',
  async (
    {
      creator,
      newContact,
      supplierId,
      apolloClient
    }: { creator: string; newContact: UserInfo; supplierId: string; apolloClient: ApolloClient<object> },
    { dispatch }
  ) => {
    try {
      dispatch(setIsLoading(true));
      dispatch(setIsSuccessAlertVisible(false));

      // Fetch the vendor entity to get the parentVendorEntityId
      const response: ApolloQueryResult<any> = await apolloClient.query({
        query: getVendorQuery,
        variables: {
          versionedId: {
            id: {
              value: supplierId
            }
          }
        }
      });

      const vendorResult = response.data as GetVendorQueryResult;
      const parentVendorEntityId: string | null = _get(
        vendorResult,
        'getVendor.vendorOutput.vendorEntityDetails.parentVendorEntityId',
        null
      );

      if (parentVendorEntityId === null) {
        throw new Error('Parent Vendor Entity ID is null');
      }

      // Fetch existing users for the business entity
      const { data }: { data: { getUsersForBusinessEntityId: GetUsersForBusinessEntityIdResult } } =
        await apolloClient.query({
          query: getUsersForBusinessEntityIdQuery,
          variables: {
            businessEntityId: supplierId,
            maxResults: 100
          }
        });
      const fetchedContacts = data.getUsersForBusinessEntityId.users!;
      const existingEmails =
        fetchedContacts.flatMap((user: { emails: any[] }) => user.emails.map((email) => email.value)) || [];

      if (existingEmails.includes(newContact.email)) {
        console.log('Duplicate contact found:', newContact.email);
        throw new Error('Duplicate contact');
      }

      const vendorUserInfo: VendorUserInfo = {
        ...newContact,
        businessEntities: [supplierId],
        corporation: parentVendorEntityId
      };

      const { userId } = await createVendorUser(apolloClient, vendorUserInfo);

      const initialPermissions: PermissionActionClass[] = [
        { permissionId: 'smart_external_user_access', actionClass: 'READ' },
        { permissionId: 'smart_external_user_access', actionClass: 'EDIT' }
      ];

      await assignGrant({
        client: apolloClient,
        principalId: userId,
        actorType: ActorTypes.USER,
        permissions: initialPermissions,
        creator
      });

      // Upon successful contact creation, we set isSuccessful true and add new contact email to temp list
      dispatch(setIsSuccessful(true));
      dispatch(setIsSuccessAlertVisible(true));
      dispatch(addPendingContact(newContact.email));
    } catch (error) {
      if (error instanceof Error && error.message === 'Duplicate contact') {
        dispatch(setIsDuplicateError(true));
      } else if (error instanceof Error && error.message === ERROR_MESSAGES.USER_ALREADY_EXISTS) {
        dispatch(setIsUserExistsError(true));
      } else if (error instanceof Error && error.message === ERROR_MESSAGES.USER_DISABLED) {
        dispatch(setIsDisabledUserError(true));
      }
      console.error(error);
      dispatch(setIsSuccessful(false));
    } finally {
      dispatch(setIsLoading(false));
    }
  }
);

export default createContactSlice.reducer;
